// CSV export for the object list pages, built in the browser from rows the API
// already returned. Values are the raw API values (ISO dates, plain numbers),
// so a spreadsheet can sort and sum them; headers are the column labels.

import { type ColumnDef, toCsv, type CsvCell } from '@olytic/foundation';

import type { ObjectRow } from './objectsApi';

/** The most rows "Export view" will fetch, in pages of EXPORT_PAGE_SIZE. */
export const EXPORT_ROW_LIMIT = 5000;
export const EXPORT_PAGE_SIZE = 200;

function cell(value: unknown): CsvCell {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
  return JSON.stringify(value);
}

/** The shown columns, in order, as CSV with a header row. */
export function rowsToCsv(columns: ColumnDef<ObjectRow>[], rows: ObjectRow[]): string {
  const header = columns.map((column) => column.label);
  // The raw value where the row has the column's key; `get` only for derived columns (a contact's name).
  const body = rows.map((row) =>
    columns.map((column) => cell(Object.hasOwn(row, column.key) || !column.get ? row[column.key] : column.get(row))),
  );
  return toCsv([header, ...body]);
}

/** e.g. "accounts-active-clients-2026-10-14.csv". */
export function csvFileName(objectKey: string, viewLabel: string, today: string, suffix = ''): string {
  const slug = (text: string) =>
    text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  return `${[slug(objectKey), slug(viewLabel), suffix, today].filter(Boolean).join('-')}.csv`;
}

/** Hands the text to the browser as a file download. */
export function downloadCsv(fileName: string, csv: string): void {
  // A byte-order mark so Excel reads the file as UTF-8.
  const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
